import type { HTMLMotionProps } from "motion/react"
import { memo, useMemo } from "react"
import { Link } from "@tanstack/react-router"
import { motion } from "motion/react"
import {
	IconCheckOutline18 as CheckIcon,
	IconChevronDownOutline18 as ChevronDown,
	IconXmarkOutline18 as XmarkIcon,
} from "nucleo-ui-outline-18"

import type { Issue, Project } from "@productlane/zero"
import { cn } from "@productlane/lib/src/cn"
import {
	pendingTargets,
	summarizePortals,
} from "@productlane/lib/src/portal-visibility"
import {
	AvatarStack,
	DropdownMenu,
	DropdownMenuItem,
	IconButton,
	IssueStatusIcon,
	Tooltip,
} from "@productlane/ui"

import { RoleCheck } from "@/components/shared/role-check"
import { PortalAvatar } from "@/components/shared/visibility-toggle"
import { useDetailLinkProps } from "@/lib/detail-drawer"
import { usePortalEntries } from "@/lib/hooks/use-portal-entries"
import { useVisibilityActions } from "@/lib/hooks/useVisibilityActions"
import { getProjectIcon } from "@/lib/projects"

type ProjectOrIssue =
	| {
			type: "project"
			data: Project
	  }
	| {
			type: "issue"
			data: Issue
	  }

type Props = HTMLMotionProps<"div"> &
	ProjectOrIssue & {
		maxPainLevel?: number
	}

export const PendingPortalItem = memo(
	({ type, data, className, maxPainLevel = 0 }: Props) => {
		const { updateVisibility, publishOn } = useVisibilityActions()
		const { entries, namedPortalIds } = usePortalEntries()
		const multiPortal = entries.length > 0
		const targets = useMemo(
			() => pendingTargets(data, namedPortalIds),
			[data, namedPortalIds],
		)
		const targetSummary = summarizePortals(targets, entries)
		const linkProps = useDetailLinkProps(type, data.id)

		const Icon = useMemo(() => {
			if (type === "project") {
				return getProjectIcon(data.icon)
			}
		}, [type, data])

		const painLevelPercent = useMemo(() => {
			let relativeScore = 0
			if (maxPainLevel) {
				relativeScore = (data?.totalScore ?? 0) / maxPainLevel
			}
			return relativeScore * 100
		}, [data.totalScore, maxPainLevel])
		return (
			<motion.div
				layout
				exit={{ opacity: 0, scale: 0.95 }}
				transition={{ duration: 0.15 }}
				className={cn("w-full overflow-hidden", className)}
			>
				<Link
					{...linkProps}
					className="grid min-h-[2.8rem] items-center gap-4 pb-2 pl-6 pr-5 pt-2.5 hover:bg-gray-100 dark:hover:bg-gray-800"
					style={{
						gridTemplateColumns: multiPortal
							? "auto 1fr auto auto auto"
							: "auto 1fr auto auto",
					}}
				>
					{/* Icon column */}
					<div className="flex items-start justify-center">
						{type === "project" && Icon ? (
							<Icon
								className="h-4 w-4 flex-none"
								style={{ color: data.color ?? "" }}
							/>
						) : type === "issue" ? (
							<IssueStatusIcon
								className="h-3.5 w-3.5 flex-none"
								color={data.statusColor ?? ""}
								status={data.status}
							/>
						) : null}
					</div>

					{/* Content column - flexible width */}
					<div className="min-w-0 overflow-hidden">
						<div className="min-w-0 max-w-full overflow-hidden truncate text-sm">
							<div className="truncate font-medium">
								{type === "project" ? data.name : data.title}
							</div>
						</div>
					</div>

					{/* Score column */}
					<div className="flex w-20 flex-none items-center gap-2 text-gray-700 dark:text-gray-300">
						<div className="relative h-1 w-10 rounded bg-gray-200 dark:bg-gray-800">
							<div
								className="absolute h-1 rounded bg-green-500"
								style={{ width: `${painLevelPercent}%` }}
							/>
						</div>
						<p className="w-7 text-sm">{data.totalScore}</p>
					</div>

					{multiPortal && (
						<Tooltip label={`Publishes on ${targetSummary}`}>
							<div className="flex flex-none items-center">
								<AvatarStack
									size="xs"
									roundness="sm"
									avatars={entries
										.filter((e) => targets.includes(e.id))
										.map((e) => ({
											name: e.name,
											image: e.logoUrl,
											colorSeed: e.colorSeed,
										}))}
								/>
							</div>
						</Tooltip>
					)}

					{/* Actions column */}
					<RoleCheck not={["VIEWER"]}>
						<div className="flex flex-row items-start gap-3">
							<Tooltip label="Hide from portal">
								<IconButton
									variant="outline"
									Icon={XmarkIcon}
									onClick={async (e) => {
										e.preventDefault()
										e.stopPropagation()
										await updateVisibility({
											id: data.id,
											type,
											value: false,
										})
									}}
								/>
							</Tooltip>
							<Tooltip
								label={
									multiPortal ? `Show on ${targetSummary}` : "Show in portal"
								}
							>
								<IconButton
									variant="outline"
									Icon={CheckIcon}
									onClick={async (e) => {
										e.preventDefault()
										e.stopPropagation()
										if (multiPortal) {
											await publishOn({
												id: data.id,
												type,
												portalIds: targets,
												namedPortalIds,
											})
											return
										}
										await updateVisibility({
											id: data.id,
											type,
											value: true,
										})
									}}
								/>
							</Tooltip>
							{multiPortal && (
								<div
									role="presentation"
									onClick={(e) => {
										e.preventDefault()
										e.stopPropagation()
									}}
								>
									<DropdownMenu
										className="w-60"
										contentProps={{ side: "bottom", align: "end" }}
										trigger={
											<IconButton
												variant="outline"
												Icon={ChevronDown}
												aria-label="Publish on one portal"
											/>
										}
									>
										<div className="px-2 pb-1 pt-1.5 text-xs font-medium text-secondary">
											Publish on
										</div>
										{entries.map((entry) => (
											<DropdownMenuItem
												key={entry.id}
												className="gap-2"
												onSelect={() => {
													void publishOn({
														id: data.id,
														type,
														portalIds: [entry.id],
														namedPortalIds,
													})
												}}
											>
												<PortalAvatar entry={entry} />
												<span className="min-w-0 flex-1 truncate">
													{entry.name}
												</span>
											</DropdownMenuItem>
										))}
									</DropdownMenu>
								</div>
							)}
						</div>
					</RoleCheck>
				</Link>
			</motion.div>
		)
	},
)
